import { useEffect, useRef } from 'react';

/**
 * useEffectSkipFirstEffect 函数用于在 useEffect 中跳过第一次渲染的效果。
 *
 * @param {Function} effect - 需要执行的副作用函数
 * @param {Array} deps - 依赖项数组
 */
export function useEffectSkipFirstEffect(effect, deps) {
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    effect();
  }, deps);
}

// 是否为对象(下面的方法有依赖，勿删)
export function isObject(obj) {
  return Object.prototype.toString.call(obj) === '[object Object]';
}

// 是否为空对象(下面的方法有依赖，勿删)
export function isEmptyObject(data) {
  try {
    return JSON.stringify(data) === '{}';
  } catch (e) {
    return false;
  }
}

// 是否为火狐浏览器
export function isFirefox() {
  return window.navigator.userAgent.indexOf('Firefox') >= 0;
}

export function isIpadCheck() {
  /*
   * ipad环境判断更新
   * iOS pre 13 以前以ua作判断，13后以platform及maxTouchPoints做判断
   */
  const isIPad =
    navigator.userAgent.match(/(iPad)/) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return isIPad;
  // 作者：CocoaKier
  // 链接：https://juejin.cn/post/6865658712665620494
  // 来源：稀土掘金
  // 著作权归作者所有。商业转载请联系作者获得授权，非商业转载请注明出处。
}

// 是否PC环境
export function isPCCheck() {
  const ua = navigator.userAgent.toLowerCase();
  const isPC = !/applewebkit.*mobile.*/.test(ua);
  const isIpad = isIpadCheck();
  if (isIpad) {
    return true;
  }
  return isPC;
}

export function isIframeAndMobile() {
  try {
    const isIframe = window.self !== window.top;
    const isMobile = window.innerWidth < 1024;

    return isIframe && isMobile;
  } catch (e) {
    return false;
  }
}

// 是否在PC端
export const isInPc = isPCCheck();

// 获取url上name的值
export function getQueryString(name) {
  const reg = new RegExp(`(^|&)${name}=([^&]*)(&|$)`);
  const { search } = window.location;
  if (search) {
    const r = search.substr(1).match(reg);
    if (r != null) return decodeURIComponent(r[2]);
    return '';
  }
  return '';
}

/**
 * 设置url 参数
 */
export function setQueryString(key, val, removeKeyList = []) {
  // 传进想设置querystring的key和value
  const path = window.location.href.split('?')[0]; // 获取地址栏 "?"后的内容
  const search = window.location.search.substr(1); // 获取地址栏 "?"后的内容
  const query = {};
  if (search) {
    search.split('&').forEach((item) => {
      const arr = item.split('=');
      // eslint-disable-next-line prefer-destructuring
      query[arr[0]] = arr[1];
    });
    if (Array.isArray(removeKeyList) && removeKeyList.length > 0) {
      removeKeyList.forEach((itemKey) => {
        if (itemKey && typeof itemKey === 'string') {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete query[itemKey];
        }
      });
    }
  }
  query[key] = val;
  const queryArr: string[] = [];
  for (const p in query) {
    if (Object.prototype.hasOwnProperty.call(query, p)) {
      queryArr.push(`${p}=${query[p]}`);
    }
  }
  return `${path}?${queryArr.join('&')}`;
}

/**
 * 设置多个url 参数
 * 传进想设置querystring的Object对象，Object包含key和value
 */
export function setManyQueryString(paramsObj, removeKeyList, opt) {
  if (!removeKeyList?.length) {
    removeKeyList = [];
  }
  const search = window.location.search.substr(1); // 获取地址栏 "?"后的内容
  let query = {};
  if (search) {
    search.split('&').forEach((item) => {
      const arr = item.split('=');
      // eslint-disable-next-line prefer-destructuring
      query[arr[0]] = arr[1];
    });
    // 移除需要删除的参数
    if (Array.isArray(removeKeyList) && removeKeyList.length > 0) {
      removeKeyList.forEach((itemKey) => {
        if (itemKey && typeof itemKey === 'string') {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete query[itemKey];
        }
      });
    }
  }
  if (isObject(paramsObj) && !isEmptyObject(paramsObj)) {
    query = Object.assign(query, paramsObj);
  }
  const queryArr: string[] = [];
  for (const p in query) {
    // 移除参数为null或undefined或空字符
    if (
      Object.prototype.hasOwnProperty.call(query, p) &&
      query[p] !== null &&
      query[p] !== undefined &&
      query[p] !== ''
    ) {
      queryArr.push(`${p}=${query[p]}`);
    }
  }
  let queryStr = '';
  if (queryArr && queryArr.length > 0) {
    queryStr = `?${queryArr.join('&')}`;
  }
  const newOpt = { isHasOrigin: true, isHasPathname: true, ...opt };
  const { isHasOrigin, isHasPathname } = newOpt;
  if (isHasOrigin) {
    const path = window.location.href.split('?')[0]; // 获取地址栏 "?"后的内容
    return `${path}${queryStr}`;
  }
  if (!isHasOrigin && isHasPathname) {
    const { pathname } = window.location;
    return `${pathname}${queryStr}`;
  }
  return queryStr;
}

// 获取url上的参数,返回一个对象
export function getUrlParams(url = window.location.href): any {
  const searchReg = /([^&=?]+)=([^&#?]+)/g;
  const urlParams = {};
  let match;
  let value;
  let name;
  // eslint-disable-next-line no-cond-assign
  while ((match = searchReg.exec(url))) {
    // eslint-disable-next-line prefer-destructuring
    name = match[1];
    // eslint-disable-next-line prefer-destructuring
    value = match[2];
    value = value ? decodeURIComponent(value) : '';
    urlParams[name] = value;
  }
  return urlParams;
}

// 是否线上环境
export function isProduction() {
  // @ts-ignore
  return process.env.BLOCK_ENV === 'production';
}

// 是否本地开发环境
export function isLocal() {
  // @ts-ignore
  return process.env.BLOCK_ENV === 'development';
}

// 过滤对象中的null和undefined
export function filterNullAndUndefined(obj) {
  if (!isObject(obj) || isEmptyObject(obj)) {
    return {};
  }
  const newObj = {};
  Object.keys(obj).forEach((it) => {
    if (obj[it] !== undefined && obj[it] !== null) {
      newObj[it] = obj[it];
    }
  });
  return newObj;
}

export function Object2Query(queryInfo: Object) {
  let queryStr = '';
  if (isObject(queryInfo) && !isEmptyObject(queryInfo)) {
    const queryList: string[] = [];
    for (const key in queryInfo) {
      if (Object.prototype.hasOwnProperty.call(queryInfo, key)) {
        let value = queryInfo[key];
        if (Array.isArray(value) || isObject(value)) {
          try {
            value = encodeURIComponent(JSON.stringify(value));
          } catch (error) {
            console.error('序列化参数出错');
            value = '';
          }
        }
        // 过滤值为undefined的key
        if (value !== undefined && value !== null) {
          queryList.push(`${key}=${value}`);
        }
      }
    }
    queryStr = queryList.join('&');
  }
  return queryStr;
}

// 判断哪种浏览器
export function whichBrowserInfo() {
  const ua = window.navigator.userAgent.toLowerCase();
  return {
    isFirefoxBrowser: /firefox/g.test(ua), // 火狐
    isSafariBrowser: /safari/g.test(ua) && !/chrome/g.test(ua) && !/firefox/g.test(ua), // 是否Safari浏览器
  };
}

// 中国大陆手机号做11位分割
export function addBlankForPhoneNum(phoneNumber) {
  if (typeof phoneNumber !== 'string') {
    return '';
  }
  let _phoneNumber = '';
  _phoneNumber = phoneNumber.replace(/\D/g, '').substring(0, 11);
  if (_phoneNumber.length > 3 && _phoneNumber.length < 8) {
    _phoneNumber = `${_phoneNumber.substr(0, 3)} ${_phoneNumber.substr(3)}`;
  } else if (_phoneNumber.length >= 8) {
    _phoneNumber = `${_phoneNumber.substr(0, 3)} ${_phoneNumber.substr(3, 4)} ${_phoneNumber.substr(7)}`;
  }
  return _phoneNumber;
}

/**
 * @description: 判断是否是超链接
 * @param {string} url
 * @return {boolean}
 */
export function isUrl(url) {
  const exp = /^https?:\/\/(([a-zA-Z0-9_-])+(\.)?)*(:\d+)?(\/((\.)?(\?)?=?&?[a-zA-Z0-9_-](\?)?)*)*$/i;
  const objExp = new RegExp(exp);
  return objExp.test(url) === true;
}

export function isWeiXin(): boolean {
  const ua = window.navigator.userAgent.toLowerCase();
  const match = ua.match(/MicroMessenger/i);
  if (match === null) {
    return false;
  }
  if (match.includes('micromessenger')) {
    return true;
  }
  return false;
}
